import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

type PipPosition = { x: number; y: number }

type Options = {
  /** Default inset from bottom-right corner in px. */
  defaultInset?: { bottom: number; right: number }
  /** PiP width/height estimates for clamping before layout measure. */
  pipSize?: { width: number; height: number }
}

/**
 * Drag a picture-in-picture element within a container using pointer events.
 * Returns position style + pointer handlers for the PiP node.
 */
export function useDraggablePip(containerRef: React.RefObject<HTMLElement | null>, options: Options = {}) {
  const { defaultInset = { bottom: 96, right: 16 }, pipSize = { width: 112, height: 144 } } = options
  const [position, setPosition] = useState<PipPosition | null>(null)
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)

  const getDefaultPosition = useCallback((): PipPosition => {
    const container = containerRef.current
    if (!container) {
      return { x: window.innerWidth - pipSize.width - defaultInset.right, y: window.innerHeight - pipSize.height - defaultInset.bottom }
    }
    const rect = container.getBoundingClientRect()
    return {
      x: rect.width - pipSize.width - defaultInset.right,
      y: rect.height - pipSize.height - defaultInset.bottom,
    }
  }, [containerRef, defaultInset.bottom, defaultInset.right, pipSize.height, pipSize.width])

  const clampPosition = useCallback(
    (x: number, y: number): PipPosition => {
      const container = containerRef.current
      const w = container?.clientWidth ?? window.innerWidth
      const h = container?.clientHeight ?? window.innerHeight
      const pipW = pipSize.width
      const pipH = pipSize.height
      return {
        x: Math.max(8, Math.min(x, w - pipW - 8)),
        y: Math.max(8, Math.min(y, h - pipH - 8)),
      }
    },
    [containerRef, pipSize.height, pipSize.width],
  )

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      const container = containerRef.current
      if (!container) return
      const current = position ?? getDefaultPosition()
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: current.x,
        originY: current.y,
      }
      if (!position) setPosition(current)
    },
    [containerRef, getDefaultPosition, position],
  )

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!dragRef.current) return
      const dx = e.clientX - dragRef.current.startX
      const dy = e.clientY - dragRef.current.startY
      setPosition(clampPosition(dragRef.current.originX + dx, dragRef.current.originY + dy))
    },
    [clampPosition],
  )

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (dragRef.current) {
      dragRef.current = null
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  const pipStyle = position
    ? { left: position.x, top: position.y, right: 'auto' as const, bottom: 'auto' as const }
    : { right: defaultInset.right, bottom: defaultInset.bottom, left: 'auto' as const, top: 'auto' as const }

  return {
    pipStyle,
    pipHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  }
}
